'use client'
import * as React from 'react'

import { createContext } from '~logic/lib/react'

import { AuthMachineProvider, useAuthMachineCtx } from './service/AuthMachineProvider'
import { useAuthContextValues } from './utils/useAuthContextValues'
import { useAuthStateValues } from './utils/useAuthStateValues'
import { useAuthUpdaters } from './utils/useAuthUpdaters'

type TUpdaters = ReturnType<typeof useAuthUpdaters>
type TContextValue = ReturnType<typeof useAuthContextValues>
type TStateValue = ReturnType<typeof useAuthStateValues>

const [Provider, useAuthCtx] = createContext<TContextValue>(`Auth`)
const [UpdateProvider, useLoginCtx] = createContext<TUpdaters>('Login')
const [StateProvider, useAuthStateCtx] = createContext<TStateValue>('AuthState')

type TAuthStateProps = {
  isLoginEnabled: FirstParam<typeof useAuthContextValues>['isLoginEnabled']
}

type TProps = RequiredChildren & TAuthStateProps & PropsOf<typeof AuthMachineProvider>

/**
 * Provides the auth state,
 * use following custom hooks to consume the context values:
 *
 *  - `useAuthCtx`: to get the `authToken` & the `userId`
 *  - `useLoginCtx`: to log the user in/out
 *  - `useAuthStateCtx`: to check the status of the `authMachine`
 */
export const AuthProvider: React.FC<TProps> = ({ children, isLoginEnabled, ...machineProps }) => {
  return (
    <AuthMachineProvider {...machineProps}>
      <AuthStateProvider isLoginEnabled={isLoginEnabled}>{children}</AuthStateProvider>
    </AuthMachineProvider>
  )
}

export { useAuthCtx, useLoginCtx, useAuthStateCtx }

// combined providers of the auth state
// exported above within the `AuthProvider`
// so it can be easily used in the app together with the machine provider as one unit
const AuthStateProvider: React.FC<RequiredChildren & TAuthStateProps> = ({
  children,
  isLoginEnabled,
}) => {
  const machine = useAuthMachineCtx()

  const updaters = useAuthUpdaters({ machine })
  const state = useAuthStateValues({ machine })
  const context = useAuthContextValues({ machine, isLoginEnabled })

  return (
    <UpdateProvider value={updaters}>
      <Provider value={context}>
        <StateProvider value={state}>{children}</StateProvider>
      </Provider>
    </UpdateProvider>
  )
}
